import { Toaster } from "@/components/ui/toaster";
import React from "react";
import { Navigate, Route, Routes, useLocation, matchPath } from "react-router-dom";
import { TOP_HOST } from "./api/_client";
import { useUser } from "./api/user";
import AgentSession from "./components/agent";
import { GenericErrorComponent } from "./components/error-boundary";
import LoadingSpinner from "./components/loading-spinner";
import { SessionTimeControl } from "./components/session-control";
import { OrganizationSwitcher, Sidebar } from "./components/sidebar";
import { Confirmation } from "./components/ui/confirmation";
import { SimpleLink } from "./components/ui/simple-link";
import { type AgentViewDescription } from "./providers/agent-provider";
import { AgentProvider } from "./providers/agent-provider.component";
import { useAppContext } from "./providers/app-context";
import { ROUTES } from "./routes";
import PasswordReset from "./views/password-reset";
import SignInView from "./views/sign-in";
import SignUpView from "./views/sign-up";
import VerifyEmail from "./views/verify-email";

const RootPage = () => {
  const { logOut } = useAppContext();
  const { setHideSidebar } = useAppContext();

  React.useEffect(() => {
    setHideSidebar(true);
  }, [setHideSidebar]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold">Welcome</h1>
        <p className="mt-4 text-lg">Choose your organization</p>
        <OrganizationSwitcher className="w-full min-w-96" />
        <SimpleLink onClick={() => logOut()}>Log out</SimpleLink>
      </div>
    </div>
  );
};

function App() {
  const {
    initialized,
    collapseSidebar,
    showAbsoluteSpinner,
    actingOrganization,
    globalError,
    showMobileSidebar,
    setShowMobileSidebar,
    hideSidebar,
    setHideSidebar,
  } = useAppContext();
  const user = useUser();
  const location = useLocation();
  const [isAgentMinimized, setIsAgentMinimized] = React.useState(true);
  const [hideAgent, setHideAgent] = React.useState(false);

  // Helper function to extract params from pathname based on route pattern
  const extractParamsFromPath = (
    pathname: string,
    route: { path?: string } | null,
  ) => {
    if (!route?.path) return {};

    let routePath = route.path;

    // Build the full path for child routes
    const parentRoute = ROUTES.find(r => {
      if (r.match) {
        const basePath = r.match.replace("/*", "");
        return pathname.startsWith(basePath);
      }
      return false;
    });

    if (parentRoute && parentRoute.children) {
      const basePath = parentRoute.match
        ? parentRoute.match.replace("/*", "")
        : parentRoute.path.replace(/\/$/, "");
      routePath = basePath + routePath;
    }


    // Use matchPath to extract params
    const match = matchPath(routePath, pathname);

    return match?.params || {};
  };
  const [currentRoute, setCurrentRoute] = React.useState(() => {
    // Helper function to find matching route (including children)
    const findMatchingRoute = (pathname: string) => {
      for (const route of ROUTES) {
        // Check if route has children
        if (route.children) {
          const basePath = route.match
            ? route.match.replace("/*", "")
            : route.path.replace(/\/$/, "");

          // Check each child route using matchPath
          for (const child of route.children) {
            const fullChildPath = basePath + child.path;
            const match = matchPath(fullChildPath, pathname);

            if (match) {
              return child;
            }
          }

          // If no child matches but path starts with base, return parent route
          if (pathname.startsWith(basePath)) {
            return route;
          }
        } else {
          // Handle routes without children
          if (route.match) {
            const basePath = route.match.replace("/*", "");
            if (pathname.startsWith(basePath)) {
              return route;
            }
          } else if (pathname === route.path) {
            return route;
          }
        }
      }
      return null;
    };

    return findMatchingRoute(location.pathname);
  });

  const currentViewDescription = React.useMemo<
    AgentViewDescription | undefined
  >(() => {
    const routeWithDescription = currentRoute as {
      description?: (...args: unknown[]) => AgentViewDescription;
    };
    return routeWithDescription?.description?.(
      extractParamsFromPath(location.pathname, currentRoute),
    );
  }, [currentRoute, location.pathname]);

  React.useEffect(() => {
    // Helper function to find matching route (including children)
    const findMatchingRoute = (pathname: string) => {
      for (const route of ROUTES) {
        // Check if route has children
        if (route.children) {
          const basePath = route.match
            ? route.match.replace("/*", "")
            : route.path.replace(/\/$/, "");

          // Check each child route using matchPath
          for (const child of route.children) {
            const fullChildPath = basePath + child.path;
            const match = matchPath(fullChildPath, pathname);

            if (match) {
              return child;
            }
          }

          // If no child matches but path starts with base, return parent route
          if (pathname.startsWith(basePath)) {
            return route;
          }
        } else {
          // Handle routes without children
          if (route.match) {
            const basePath = route.match.replace("/*", "");
            if (pathname.startsWith(basePath)) {
              return route;
            }
          } else if (pathname === route.path) {
            return route;
          }
        }
      }
      return null;
    };

    const matchedRoute = findMatchingRoute(location.pathname);
    setCurrentRoute(matchedRoute || null);
  }, [location.pathname]);

  React.useEffect(() => {
    if (TOP_HOST === window.location.host) {
      setHideSidebar(true);
    }
  }, [user.data, setHideSidebar]);

  React.useEffect(() => {
    if (showMobileSidebar) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [showMobileSidebar]);

  React.useEffect(() => {
    if (
      (user.data && user.data?.email_verified === false) ||
      TOP_HOST === window.location.host
    ) {
      setHideAgent(true);
    } else {
      setHideAgent(false);
    }
  }, [user.data]);

  if (globalError) return <GenericErrorComponent />;
  if (!initialized) return <LoadingSpinner delay={false} />;

  return (
    <div className="flex flex-row overflow-x-hidden">
      {!hideSidebar && (
        <>
          {/* mobile sidebar */}
          <div
            className={`absolute z-[51] block transition-all lg:hidden ${
              showMobileSidebar ? "left-0" : "left-[-260px]"
            } lg:left-0`}
          >
            {user.data && <Sidebar className={`w-[260px]`} collapsed={false} />}
          </div>
          {/* mobile sidebar backdrop */}
          <div
            className={`fixed left-0 top-0 z-50 h-full w-full bg-black opacity-50 lg:z-[-50] lg:hidden ${
              !showMobileSidebar && "hidden"
            }`}
            onClick={() => setShowMobileSidebar((prev: boolean) => !prev)}
          />
          {/* Regular sidebar */}
          <div className="absolute left-[-260px] transition-all lg:left-0">
            {user.data && (
              <Sidebar
                className={`${
                  collapseSidebar ? "w-[62px]" : "w-[260px]"
                } transition-all`}
                collapsed={collapseSidebar}
              />
            )}
          </div>
        </>
      )}
      <div
        className={`${
          hideSidebar
            ? !isAgentMinimized
              ? "max-w-[calc(100vw-380px)]"
              : ""
            : user.data
              ? collapseSidebar
                ? !isAgentMinimized
                  ? "max-w-[calc(100vw-62px-380px)] lg:ml-[62px]"
                  : "max-w-[calc(100vw-62px)] lg:ml-[62px]"
                : !isAgentMinimized
                  ? "max-w-[calc(100vw-260px-380px)] lg:ml-[260px]"
                  : "max-w-[calc(100vw-260px)] lg:ml-[260px]"
              : !isAgentMinimized
                ? "max-w-[calc(100vw-380px)] lg:ml-0"
                : "lg:ml-0"
        } w-full transition-all`}
      >
        <React.Suspense fallback={<LoadingSpinner />}>
          <>
            {user.data && user.data?.email_verified === false ? (
              <Routes>
                <Route path="/" element={<RootPage />} />
                <Route path="/verify/:uid/:token" element={<VerifyEmail />} />
                <Route path="*" element={<VerifyEmail />} />
              </Routes>
            ) : user.data ? (
              <>
                <Routes>
                  {TOP_HOST === window.location.host ? (
                    <>
                      <Route path="*" element={<RootPage />} />
                    </>
                  ) : (
                    ROUTES.map((route) => {
                      if (
                        actingOrganization &&
                        route.canAccess &&
                        !route.canAccess(actingOrganization!)
                      ) {
                        return null;
                      }
                      if (route.children) {
                        return (
                          <Route
                            path={route.match}
                            key={route.name}
                            element={
                              <Routes>
                                <Route path="/" element={<route.component />} />
                                {route.children?.map((child) => (
                                  <Route
                                    path={child.path}
                                    element={<child.component />}
                                    key={route.path + child.path}
                                  />
                                ))}
                              </Routes>
                            }
                          />
                        );
                      }
                      return (
                        <Route
                          path={route.path}
                          element={<route.component />}
                          key={route.name}
                        />
                      );
                    })
                  )}
                  <Route path="/verify/:uid/:token" element={<VerifyEmail />} />
                  <Route path="/error" element={<GenericErrorComponent />} />
                  <Route
                    path="*"
                    element={<Navigate to="/portfolios" replace />}
                  />
                </Routes>
                {!hideAgent && (
                  <AgentProvider>
                    <AgentSession
                      currentViewDescription={currentViewDescription}
                      onMinimizedChange={setIsAgentMinimized}
                    />
                  </AgentProvider>
                )}
              </>
            ) : (
              <Routes>
                <Route
                  path="/reset-password/:uidb64/:token"
                  element={<PasswordReset />}
                />
                <Route path="/join" element={<SignUpView />} />
                <Route path="/error" element={<GenericErrorComponent />} />
                <Route path="*" element={<SignInView />} />
              </Routes>
            )}
          </>
        </React.Suspense>
      </div>
      <Toaster />
      <Confirmation />
      <SessionTimeControl />

      {showAbsoluteSpinner && <LoadingSpinner absolute />}
    </div>
  );
}

export default App;
